import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useSitzung } from '../session/SessionProvider'
import { Button, Empty, Symbol, TextField } from '../ui'
import { BEREICH_GRUPPEN, sichtbareBereiche, type BereichDefinition } from '../shell/bereiche'
import { ladePins, MAX_PINS, speicherePins } from '../shell/navigation-prefs'

function BereichsLinks({ bereiche }: { bereiche: readonly BereichDefinition[] }) {
  return (
    <div className="kachel-raster">
      {bereiche.map((bereich) => (
        <Link className="kachel" key={bereich.id} to={bereich.pfad}>
          <Symbol name={bereich.symbol} />
          <span>{bereich.label}</span>
        </Link>
      ))}
    </div>
  )
}

export function Mehr() {
  const { abfrage, caps } = useSitzung()
  const me = abfrage.data
  const [parameter] = useSearchParams()
  const suchfeld = useRef<HTMLInputElement>(null)
  const bereiche = useMemo(() => sichtbareBereiche(caps), [caps])
  const memberId = me && !me.setupRequired ? me.member.id : ''
  const [pins, setPins] = useState<string[]>(() => ladePins(memberId, bereiche))
  const [suche, setSuche] = useState('')

  useEffect(() => {
    setPins(ladePins(memberId, bereiche))
  }, [bereiche, memberId])

  useEffect(() => {
    if (parameter.get('suche') === '1') suchfeld.current?.focus()
  }, [parameter])

  const anheftbar = bereiche.filter(
    (bereich) => bereich.id !== 'heute' && bereich.id !== 'einstellungen',
  )
  const gueltigePins = pins.filter((id) => anheftbar.some((bereich) => bereich.id === id))
  const angeheftet = gueltigePins
    .map((id) => bereiche.find((bereich) => bereich.id === id))
    .filter((bereich) => bereich !== undefined)
  const begriff = suche.trim().toLocaleLowerCase('de')
  const treffer = begriff
    ? bereiche.filter((bereich) => bereich.label.toLocaleLowerCase('de').includes(begriff))
    : []

  const pinUmschalten = (id: string) => {
    const naechste = gueltigePins.includes(id)
      ? gueltigePins.filter((pin) => pin !== id)
      : gueltigePins.length < MAX_PINS
        ? [...gueltigePins, id]
        : gueltigePins
    setPins(naechste)
    speicherePins(memberId, naechste)
  }

  return (
    <>
      <div className="seitenkopf">
        <div>
          <h1>Bereiche</h1>
          <p>Alle freigeschalteten Bereiche und deine mobile Navigation.</p>
        </div>
      </div>

      <TextField
        id="bereich-suchen"
        ref={suchfeld}
        type="search"
        label="Bereich suchen"
        wert={suche}
        onWert={setSuche}
        hilfe="Tastenkürzel: Strg+K oder Cmd+K"
      />

      {begriff ? (
        <section className="mehr-gruppe" aria-labelledby="suchergebnisse-titel">
          <h2 id="suchergebnisse-titel" className="typ-titel-md">
            Suchergebnisse
          </h2>
          {treffer.length > 0 ? (
            <BereichsLinks bereiche={treffer} />
          ) : (
            <Empty
              titel="Kein Bereich gefunden"
              text="Prüfe den Suchbegriff oder lösche ihn, um alle Bereiche zu sehen."
            />
          )}
        </section>
      ) : (
        <>
          {angeheftet.length > 0 ? (
            <section className="mehr-gruppe" aria-labelledby="angeheftet-titel">
              <h2 id="angeheftet-titel" className="typ-titel-md">
                Angeheftet
              </h2>
              <BereichsLinks bereiche={angeheftet} />
            </section>
          ) : null}

          {BEREICH_GRUPPEN.map((gruppe) => {
            const gruppenBereiche = bereiche.filter(
              (bereich) => bereich.gruppe === gruppe.id && !gueltigePins.includes(bereich.id),
            )
            if (gruppenBereiche.length === 0) return null
            return (
              <section
                className="mehr-gruppe"
                aria-labelledby={`mehr-${gruppe.id}-titel`}
                key={gruppe.id}
              >
                <h2 id={`mehr-${gruppe.id}-titel`} className="typ-titel-md">
                  {gruppe.label}
                </h2>
                <BereichsLinks bereiche={gruppenBereiche} />
              </section>
            )
          })}

          <section className="mehr-gruppe" aria-labelledby="navigation-anpassen-titel">
            <div>
              <h2 id="navigation-anpassen-titel" className="typ-titel-md">
                Mobile Navigation anpassen
              </h2>
              <p className="unterzeile">Wähle bis zu drei Bereiche neben Heute und Mehr.</p>
            </div>
            <div className="mehr-pins" role="group" aria-label="Angeheftete Bereiche auswählen">
              {anheftbar.map((bereich) => {
                const gewaehlt = gueltigePins.includes(bereich.id)
                return (
                  <Button
                    key={bereich.id}
                    variant={gewaehlt ? 'tonal' : 'outlined'}
                    aria-pressed={gewaehlt}
                    disabled={!gewaehlt && gueltigePins.length >= MAX_PINS}
                    onClick={() => pinUmschalten(bereich.id)}
                  >
                    {bereich.label}
                  </Button>
                )
              })}
            </div>
          </section>
        </>
      )}
    </>
  )
}
